import { Vue, Component, Prop, Model } from 'vue-property-decorator'
import uniqCid from '@/helpers/uniqCid'
import { debounce, DebouncedFunc } from '@/lib/lodash'

@Component({
  inheritAttrs: false,
})
export default class Dialog extends Vue {
  @Model('input', {
    type: Boolean,
    default: false,
  })
  private readonly value!: boolean

  @Prop({ type: Boolean, default: true })
  private readonly renderContentAfterShow!: boolean

  @Prop({ type: Boolean, default: false })
  private readonly fullscreen!: boolean
  @Prop({ type: String })
  private readonly transition?: string
  @Prop({ type: String })
  private readonly dialogClass?: string
  @Prop({ type: Boolean, default: false })
  private readonly retainFocus!: boolean

  private showedContent = false
  private showedDialog = false
  private contentDivId = uniqCid().toString()
  private toggleFullscreenClassName!: DebouncedFunc<() => void>

  private get localTransition() {
    if (!this.transition) {
      return this.fullscreen
        ? 'dialog-bottom-transition'
        : 'scroll-y-reverse-transition'
    }

    return this.transition
  }

  private toggleDialog(showed = !this.showedDialog) {
    this.showedDialog = showed
  }

  private created() {
    this.toggleFullscreenClassName = debounce(() => {
      const appEl = document.getElementById('app')

      if (!appEl) {
        return
      }

      let hasActiveFullscreenDialog = false

      for (const childEl of appEl.children) {
        if (!childEl.classList.contains('v-dialog__content')) {
          continue
        }

        hasActiveFullscreenDialog =
          !!childEl.firstElementChild?.classList.contains(
            'v-dialog--fullscreen'
          ) &&
          !!childEl.firstElementChild?.classList.contains('v-dialog--active')

        if (hasActiveFullscreenDialog) {
          break
        }
      }

      document.documentElement.classList.toggle(
        'showed-fullscreen-dialog',
        hasActiveFullscreenDialog
      )
    }, 10)
  }

  private mounted() {
    let transitionTimeoutId = 0

    this.showedDialog = this.value
    this.showedContent = this.showedDialog

    this.$watch(
      () => {
        return this.value
      },
      (value) => {
        this.showedDialog = value
      },
      {
        immediate: true,
      }
    )

    this.$watch(
      () => {
        return this.showedDialog
      },
      (showedDialog) => {
        if (transitionTimeoutId) {
          window.clearTimeout(transitionTimeoutId)
          transitionTimeoutId = 0
        }

        const dialogClassList = this.dialogClass?.split(' ')

        if (dialogClassList?.length) {
          this.$nextTick(() => {
            const dialogEl = document.getElementById(this.contentDivId)
              ?.parentElement?.parentElement

            dialogClassList.forEach((className) => {
              className = className.trim()

              if (!className) {
                return
              }

              dialogEl?.classList.add(className)
            })
          })
        }

        this.showedContent = true

        if (!showedDialog) {
          transitionTimeoutId = window.setTimeout(() => {
            this.showedContent = !this.renderContentAfterShow
            transitionTimeoutId = 0

            this.$emit('hidden')
          }, 300)
        }

        this.toggleFullscreenClassName()
      }
    )

    this.$once('hook:beforeDestroy', () => {
      if (transitionTimeoutId) {
        window.clearTimeout(transitionTimeoutId)
      }
    })
  }

  private destroyed() {
    this.toggleFullscreenClassName()
  }
}
